import { useRouter } from "next/router";
import { useState } from "react";
import OnlineStoreFooter from "@/components/common/footer/onlineStore/onlineStoreFooter";
import PageContainerWrapper from "@/components/common/pageContainer/pageContainerWrapper";
import PageTabs from "@/components/common/pageTabs/pageTabs";
import OnlineStoreTopBarWithCheckout from "@/components/common/topBar/onlineStore/onlineStoreTopBarWithCheckout";
import AffirmPromotionBanner from "@/components/online-store/affirmPromotionBanner";
import PackagesPageHead from "@/components/online-store/packagesPageHead";
import BannerImageContainer from "@/components/serviceFlow/settings/onlineStore/bannerImageContainer";
import { PRIMARY } from "@/config/mui/colorPalette";
import { MedspaOnlineStoreDataQuery } from "@/graphql/queries/onlineStore/medspaOnlineStoreData.graphql.types";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { useSelectiveEffect } from "@/hooks/misc/useSelectiveEffect";
import { FeatureFlags } from "@/utils/featureFlags";
import { DESKTOP_BANNER_AR, MOBILE_BANNER_AR } from "@/utils/onlineStore";
import OnlineStoreMembershipsContainer from "@/views/online-store/onlineStoreMembershipsContainer";
import OnlineStorePackagesContainer from "./onlineStorePackagesContainer";

export type MedspaOnlineStoreData =
  MedspaOnlineStoreDataQuery["medspa"][number];

export type MedspaMembership = MedspaOnlineStoreData["memberships"][number];

export type MedspaPackage = MedspaOnlineStoreData["packages"][number];

export enum OnlineStoreTab {
  PACKAGES = "packages",
  MEMBERSHIPS = "memberships",
}

type Props = {
  medspaData: MedspaOnlineStoreData;
  initialTab: OnlineStoreTab;
};

function OnlineStoreView({ medspaData, initialTab }: Props) {
  const {
    query: { medspaSlug, tab },
    push,
  } = useRouter();

  const tabFromQuery = tab as string;

  const [isCheckoutDrawerOpen, setIsCheckoutDrawerOpen] = useState(false);

  const [selectedTab, setSelectedTab] = useState(
    initialTab || OnlineStoreTab.PACKAGES
  );

  useSelectiveEffect(() => {
    if (tabFromQuery !== selectedTab) {
      setSelectedTab(tabFromQuery as OnlineStoreTab);
    }
  }, [tabFromQuery]);

  const onTabChangeHandler = (tab: OnlineStoreTab) => {
    setSelectedTab(tab);
    push(`/online-store/${medspaSlug}/${tab}`, undefined, { shallow: true });
  };

  const isMobile = useMoxieMediaQuery("ipad-ver", "down");

  const {
    name,
    address,
    contactPhone: phone,
    contactEmail: email,
    configuration: {
      customizationEnabled,
      customizationPrimaryColor,
      featuredDealsSectionEnabled,
    },
    additionalPublicInfo: {
      logo: logoUrl,
      enabledFeatures,
      onlineStoreMobileBanner: mobileBannerImage,
      onlineStoreDesktopBanner: desktopBannerImage,
    },
    packages,
    memberships,
    id: medspaId,
  } = medspaData;

  const hasBanner = mobileBannerImage && desktopBannerImage;

  const primaryColor = customizationEnabled
    ? customizationPrimaryColor || PRIMARY
    : PRIMARY;

  const cherryIntegrationV1Enabled = enabledFeatures.includes(
    FeatureFlags.CHERRY_INTEGRATION_V1
  );

  const packageCardRevampV1Enabled = enabledFeatures.includes(
    FeatureFlags.PACKAGE_CARD_REVAMP_V1
  );

  return (
    <>
      <PackagesPageHead medspa={medspaData} />
      <PageContainerWrapper
        topBarComponent={
          <OnlineStoreTopBarWithCheckout
            name={name}
            logoUrl={logoUrl}
            medspaId={medspaId}
            packagesData={packages}
            customizationEnabled={customizationEnabled}
            customizationPrimaryColor={customizationPrimaryColor}
            cherryIntegrationV1Enabled={cherryIntegrationV1Enabled}
            isCheckoutDrawerOpen={isCheckoutDrawerOpen}
            setIsCheckoutDrawerOpen={setIsCheckoutDrawerOpen}
          />
        }
        footerComponent={
          <OnlineStoreFooter
            name={name}
            phone={phone}
            email={email}
            address={address}
            medspaSlug={medspaSlug as string}
            customizationPrimaryColor={primaryColor}
            cherryIntegrationV1Enabled={cherryIntegrationV1Enabled}
          />
        }
        additionalBannerComponent={
          cherryIntegrationV1Enabled && <AffirmPromotionBanner />
        }
        sx={{
          minHeight: "100vh",
          height: "auto",
          py: 0,
          mb: 0,
        }}
        maxWidth="lg-client"
      >
        {hasBanner && (
          <BannerImageContainer
            image={isMobile ? mobileBannerImage : desktopBannerImage}
            aspectRatio={isMobile ? MOBILE_BANNER_AR : DESKTOP_BANNER_AR}
            sx={{ my: 5, ...(isMobile && { mt: 2, mb: 3 }) }}
          />
        )}
        <PageTabs
          variant={isMobile ? "fullWidth" : "standard"}
          tabs={[
            {
              id: OnlineStoreTab.PACKAGES,
              name: "Packages & Gift Cards",
              content: (
                <OnlineStorePackagesContainer
                  packages={packages}
                  customizationPrimaryColor={primaryColor}
                  featuredDealsSectionEnabled={featuredDealsSectionEnabled}
                  customizationEnabled={customizationEnabled}
                  medspaId={medspaId}
                  setIsCheckoutDrawerOpen={setIsCheckoutDrawerOpen}
                  packageCardRevampV1Enabled={packageCardRevampV1Enabled}
                />
              ),
            },
            {
              id: OnlineStoreTab.MEMBERSHIPS,
              name: "Memberships",
              content: (
                <OnlineStoreMembershipsContainer memberships={memberships} />
              ),
            },
          ].filter(Boolean)}
          selectedTab={selectedTab}
          onTabChange={onTabChangeHandler}
          sx={{ mt: hasBanner ? 0 : 3 }}
        />
      </PageContainerWrapper>
    </>
  );
}

export default OnlineStoreView;
